import React, { useState, useEffect } from 'react';
import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  IconButton,
  Whisper,
  Popover,
  Dropdown,
  Modal,
  Loader
} from 'rsuite';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@rsuite/icons/Search';
import { Edit, Icon, More } from '@rsuite/icons';
import { MdAdd, MdEdit, MdDelete, MdMoreVert, MdOutlineDeleteOutline } from 'react-icons/md';
import DrawerView from './DrawerView';
import EditDrawerView from './EditDrawerView';
import { getGrades, removeGrade } from '../../../services/grade.service';
import { useDispatch, useSelector } from 'react-redux';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = () => {
  const dispatch = useDispatch();
  const grades = useSelector(state => state.grade.data);
  const loading = useSelector(state => state.grade.loading);

  useEffect(() => {
    dispatch(getGrades());
  }, [dispatch]);

  const [showDrawer, setShowDrawer] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleEdit = rowData => {
    setSelectedGrade(rowData);
    setShowEditDrawer(true);
  };

  const handleDelete = rowData => {
    setSelectedGrade(rowData);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (selectedGrade && selectedGrade._id) {
      dispatch(removeGrade(selectedGrade._id));
      setShowDeleteModal(false);
    }
  };

  const renderMenu = ({ onClose, left, top, className }, ref, rowData) => {
    const handleSelect = eventKey => {
      onClose();
      switch (eventKey) {
        case 'edit':
          handleEdit(rowData);
          break;
        case 'delete':
          handleDelete(rowData);
          break;
      }
    };
    return (
      <Popover ref={ref} className={className} style={{ left, top }} full>
        <Dropdown.Menu onSelect={handleSelect}>
          <Dropdown.Item eventKey="edit">{t('Edit')}</Dropdown.Item>
          <Dropdown.Item eventKey="delete">{t('Delete')}</Dropdown.Item>
        </Dropdown.Menu>
      </Popover>
    );
  };
  const { t } = useTranslation();

  const ActionCell = ({ rowData, ...props }) => {
    return (
      <Cell {...props} className="link-group">
        <Whisper
          placement="autoVerticalEnd"
          trigger="click"
          speaker={({ onClose, left, top, className }, ref) =>
            renderMenu({ onClose, left, top, className }, ref, rowData)
          }
        >
          <IconButton appearance="subtle" icon={<Icon as={MdMoreVert} />} size="sm" />
        </Whisper>
      </Cell>
    );
  };

  const filteredGrades = grades.filter(grade =>
    grade.grade_name.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <InputGroup inside>
            <Input
              placeholder={t("Search")}
              value={searchKeyword}
              onChange={value => setSearchKeyword(value)}
            />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
        </Stack>

        <Stack spacing={6}>
          <Button appearance="primary" onClick={() => setShowDrawer(true)}>
            <Icon as={MdAdd} style={{ marginRight: 8 }} /> {t('Add Grade')}
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Loader center content="Loading..." />
      ) : (
        <Table height={Math.max(getHeight(window) - 200, 400)} data={filteredGrades}>
          <Column flexGrow={4}>
            <HeaderCell>{t('Grade Name')}</HeaderCell>
            <Cell dataKey="grade_name" />
          </Column>

          <Column width={50} flexGrow={0.5} fixed="right">
            <HeaderCell>
              {' '}
              <More />
            </HeaderCell>
            {/* <ActionCell /> */}
            <Cell style={{ padding: '6px' }}>
              {rowData => (
                <Stack>
                  <IconButton
                    onClick={() => {
                      handleEdit(rowData);
                    }}
                    size="xs"
                    icon={<Edit color="blue" />}
                    id={`edit-${rowData._id}`}
                  />
                  <IconButton
                    size="xs"
                    icon={<MdOutlineDeleteOutline color="red" />}
                    onClick={() => {
                      handleDelete(rowData);
                    }}
                    id={`delete-${rowData._id}`}
                  />
                </Stack>
              )}
            </Cell>
          </Column>
        </Table>
      )}

      <DrawerView open={showDrawer} onClose={() => setShowDrawer(false)} />

      <EditDrawerView
        open={showEditDrawer}
        grade={selectedGrade}
        onClose={() => setShowEditDrawer(false)}
      />

      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>{t('Delete Grade')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('Are you sure you want to delete this grade? This action cannot be undone.')}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowDeleteModal(false)} appearance="subtle">
            {t('Cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} appearance="primary" color="red">
            {t('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DataTable;
